import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Mail, Loader2, RotateCcw, Save, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { emailSettingsAPI } from '@/lib/api';

const GROUP_TONE = {
  Dispatch: 'bg-teal-50 text-teal-700 border-teal-200',
  Purchase: 'bg-amber-50 text-amber-700 border-amber-200',
  Quality: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  Reminders: 'bg-slate-100 text-slate-600 border-slate-200',
};

function SettingRow({ setting, onSaved }) {
  const [to, setTo] = useState((setting.to || []).join(', '));
  const [cc, setCc] = useState((setting.cc || []).join(', '));
  const [enabled, setEnabled] = useState(setting.enabled !== false);
  const [busy, setBusy] = useState(false);

  const dirty = to !== (setting.to || []).join(', ')
    || cc !== (setting.cc || []).join(', ')
    || enabled !== (setting.enabled !== false);

  const save = async () => {
    const parse = (s) => s.split(',').map(x => x.trim()).filter(Boolean);
    const bad = [...parse(cc), ...(setting.uses_to ? parse(to) : [])]
      .filter(e => !/^[^\s@,]+@[^\s@,]+\.[^\s@,]+$/.test(e));
    if (bad.length) { toast.error(`Not a valid email address: ${bad.join(', ')}`); return; }
    setBusy(true);
    try {
      await emailSettingsAPI.update(setting.key, {
        to: setting.uses_to ? parse(to) : undefined,
        cc: parse(cc),
        enabled,
      });
      toast.success(`${setting.label} recipients saved`);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save');
    } finally { setBusy(false); }
  };

  const reset = async () => {
    setBusy(true);
    try {
      await emailSettingsAPI.reset(setting.key);
      toast.success('Reverted to the default recipients');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not reset');
    } finally { setBusy(false); }
  };

  return (
    <div className="border rounded-lg p-3 space-y-2.5 bg-white" data-testid={`email-setting-${setting.key}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-slate-800">{setting.label}</span>
            <Badge variant="outline" className={`text-[10px] ${GROUP_TONE[setting.group] || ''}`}>
              {setting.group}
            </Badge>
            {setting.customised && (
              <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200">
                customised
              </Badge>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground mt-0.5">{setting.description}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-[10px] text-muted-foreground">{enabled ? 'Active' : 'Muted'}</span>
          <Switch checked={enabled} onCheckedChange={setEnabled} data-testid={`email-enabled-${setting.key}`} />
        </div>
      </div>

      <div className={`grid gap-2 ${setting.uses_to ? 'md:grid-cols-2' : ''}`}>
        {setting.uses_to && (
          <div>
            <Label className="text-[11px]">Default To</Label>
            <Input value={to} onChange={e => setTo(e.target.value)}
              placeholder="comma separated addresses" className="h-8 text-xs"
              data-testid={`email-to-${setting.key}`} />
          </div>
        )}
        <div>
          <Label className="text-[11px]">Standard CC {setting.uses_to ? '' : '(internal copies)'}</Label>
          <Input value={cc} onChange={e => setCc(e.target.value)}
            placeholder="comma separated addresses" className="h-8 text-xs"
            data-testid={`email-cc-${setting.key}`} />
        </div>
      </div>

      <div className="flex items-center justify-between gap-2">
        <p className="text-[10px] text-muted-foreground">
          Default: {(setting.default_cc || []).join(', ') || '—'}
          {setting.updated_by_name ? ` · last changed by ${setting.updated_by_name}` : ''}
        </p>
        <div className="flex gap-1.5">
          {setting.customised && (
            <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={reset} disabled={busy}
              data-testid={`email-reset-${setting.key}`}>
              <RotateCcw className="w-3 h-3 mr-1" /> Default
            </Button>
          )}
          <Button size="sm" className="h-7 text-[11px] bg-slate-900 hover:bg-slate-800"
            onClick={save} disabled={busy || !dirty} data-testid={`email-save-${setting.key}`}>
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />} Save
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function EmailSettingsPage() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await emailSettingsAPI.list();
      setSettings(res.data?.settings || []);
      setGroups(res.data?.groups || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load email settings');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="p-5 max-w-5xl mx-auto space-y-4" data-testid="email-settings-page">
      <div className="flex items-center gap-3">
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => navigate('/')}>
          <ArrowLeft className="w-3.5 h-3.5 mr-1" /> ERP Home
        </Button>
      </div>
      <div>
        <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
          <Mail className="w-5 h-5 text-teal-600" /> Email Configuration
        </h1>
        <p className="text-xs text-muted-foreground mt-1">
          Who gets copied on what. These lists replace the addresses that used to be hardcoded —
          the Default To is always added to dispatch emails, on top of the customer and programme contacts.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground py-10">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : groups.map(group => (
        <Card key={group}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{group}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2.5">
            {settings.filter(s => s.group === group).map(s => (
              <SettingRow key={s.key} setting={s} onSaved={load} />
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
